import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle, Printer } from 'lucide-react';
import {
  buildReceiptHTML,
  eposPrintReceipt,
  printReceipt,
} from '../utils/receiptPrinter';

// ----------------------------------------------------------------
// ReceiptModal — receipt preview + print (shared)
// ----------------------------------------------------------------
// Renders a faithful preview of the 80mm receipt and prints to the
// TM-T88VII. Prefers the ePOS-Print network path; falls back to the
// browser print dialog when no printer IP is set or ePOS fails. Used
// both for post-transaction receipts and for reprints (receipt.reprint).

const fmtCents = (cents) => {
  if (cents == null) return '$0.00';
  return (Number(cents) / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
};

export default function ReceiptModal({ receipt, copies, autoPrint, printerHost, onClose }) {
  const printedRef = useRef(false);
  const previewHtml = buildReceiptHTML(receipt, { copyTotal: copies });
  const [status, setStatus] = useState('idle'); // idle | printing | sent | browser
  const [message, setMessage] = useState('');

  const doPrint = useCallback(async () => {
    setStatus('printing');
    setMessage('');
    const host = (printerHost || '').trim();
    if (host) {
      const res = await eposPrintReceipt(receipt, copies, host);
      if (res.ok) {
        setStatus('sent');
        return;
      }
      setMessage(
        (res.error || 'Direct printing failed.') +
          ' Used the browser print dialog instead.'
      );
      setStatus('browser');
      await printReceipt(receipt, copies);
      return;
    }
    setStatus('browser');
    await printReceipt(receipt, copies);
  }, [receipt, copies, printerHost]);

  useEffect(() => {
    if (autoPrint && !printedRef.current) {
      printedRef.current = true;
      const t = setTimeout(doPrint, 350);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [autoPrint, doPrint]);

  const baseTitle =
    receipt.type === 'deposit'
      ? 'Deposit'
      : receipt.type === 'credit_draw'
        ? 'Credit drawn'
        : receipt.type === 'credit_payment'
          ? 'Balance paid'
          : 'Withdrawal';
  const headerTitle = receipt.reprint ? `${baseTitle} — reprint` : `${baseTitle} complete`;
  const copyText = copies + ' ' + (copies === 1 ? 'copy' : 'copies');
  const printing = status === 'printing';

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-md p-6 space-y-4">
        <div className="flex flex-col items-center text-center gap-1">
          <div className="w-12 h-12 rounded-full bg-emerald-500/15 flex items-center justify-center">
            <CheckCircle size={26} className="text-emerald-400" />
          </div>
          <h3 className="text-lg font-semibold text-white">{headerTitle}</h3>
          <p className="text-sm text-gray-400">
            {fmtCents(receipt.amountCents)} · {receipt.playerName}
          </p>
        </div>

        <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 flex justify-center">
          <iframe
            title="Receipt preview"
            srcDoc={previewHtml}
            className="bg-white rounded"
            style={{ width: 288, height: 320, border: 'none' }}
            onLoad={(e) => {
              try {
                const h = e.target.contentWindow.document.body.scrollHeight;
                e.target.style.height =
                  String(Math.min(Math.max(h + 6, 200), 470)) + 'px';
              } catch (err) {
                /* preview height stays at its default */
              }
            }}
          />
        </div>

        <div className="text-center text-xs">
          {status === 'idle' && (
            <span className="text-gray-500">
              {printerHost
                ? 'Print will send ' + copyText + ' to ' + printerHost + '.'
                : 'No printer IP set — Print uses the browser dialog.'}
            </span>
          )}
          {printing && (
            <span className="text-gray-400">Sending {copyText} to the printer…</span>
          )}
          {status === 'sent' && (
            <span className="text-emerald-400">Sent {copyText} to the printer.</span>
          )}
          {status === 'browser' && (
            <span className="text-amber-400">
              {message || 'Printed ' + copyText + ' through the browser.'}
            </span>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-gray-400 hover:text-white">
            Done
          </button>
          <button
            onClick={doPrint}
            disabled={printing}
            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg disabled:opacity-50 flex items-center gap-2"
          >
            <Printer size={16} />
            {status === 'idle' ? 'Print receipt' : 'Print again'}
          </button>
        </div>
      </div>
    </div>
  );
}
